import React, { useContext } from 'react'

import {
  AppSettingsFragment,
  CourseHistoryProgressSummaryOption,
  DegreeRequirementsPanelConfiguration,
  StudentAppAuthProviderType
} from '../../generated/graphql'

const { EnabledWithIntegration } = DegreeRequirementsPanelConfiguration

/**
 * This uses the authenticated route from the student-api to get settings.
 * Should be kept separate from the public scheduler-api settings route used
 * for course search.
 */
export const defaultAppSettings: AppSettingsFragment = {
  academicPlanningSettings: {
    adminAnalyticsLabel: '',
    adminAnalyticsUrl: '',
    advisingLabel: '',
    alwaysShowRequirementCourseAndCreditCounts: false,
    disableElevio: false,
    disableElevioForStudents: false,
    disallowPrimaryPlanRemoval: false,
    disallowSwitchingPlans: false,
    degreeRequirementsPanelConfiguration: EnabledWithIntegration,
    auditRefreshNudgeInterval: 7,
    enableAdminAnalyticsLink: false,
    enableAdvisingLink: false,
    enableSubstitutionUsedCourses: false,
    advisingUrl: '',
    courseDisplayFormat: '{SubjectId} {CourseNumber} - {Title}', // for unit testing,
    courseTakenRequirementMessage: '',
    duplicateCourseMessage: '',
    enableLockAllPlanItems: false,
    defaultPlanLockedState: false,
    enableMarkAllCommentsAsRead: false,
    enableCrossListingUsedCourses: false,
    hideRequisitesOnPlanItemDetails: false,
    courseHistoryProgressSummaryOptions: [
      CourseHistoryProgressSummaryOption.InProgressUnits,
      CourseHistoryProgressSummaryOption.TotalEarnedUnits,
      CourseHistoryProgressSummaryOption.TransferUnits,
      CourseHistoryProgressSummaryOption.CourseWithdrawals
    ],
    pastTermYears: 5,
    planDisclaimer: '',
    requireTemplatePlanItems: false,
    showExceptionsOnPlanAlerts: false,
    showGradesOnPlanItems: false,
    showWithdrawnStatusOnPlanItems: false,
    hideTermsOffered: false,
    readOnlyPlansForStudents: false,
    advisorViewOwnPlan: false,
    allowPlaceholderEditInReadOnly: false
  },
  adminSetting: {
    features: []
  },
  authSettings: {
    enableStudentAppFederatedLogout: false,
    studentAppProvider: StudentAppAuthProviderType.Auth0
  },
  cartSettings: {
    cartLabel: 'Shopping Cart'
  },
  homeSettings: {
    signoutText: 'Sign Out'
  },
  sectionFieldText: {
    credit: 'Credit',
    credits: 'Credits'
  },
  text: {
    advisor: 'Advisor',
    advisorPlural: 'Advisors',
    campus: 'Campus',
    campusPlural: 'Campuses',
    course: 'Course',
    coursePlural: 'Courses',
    locationPlural: 'Locations',
    plannerTitle: 'Schedule Planner',
    register: 'Register',
    registered: 'Registered',
    schedule: 'Schedule',
    schedulePlural: 'Schedules',
    term: 'Term',
    termPlural: 'Terms'
  },
  integrationSettings: {
    auditReportType: '',
    additionalAuditReportTypes: []
  }
}

export const AppSettingsContext = React.createContext<AppSettingsFragment>(defaultAppSettings)

const useAppSettings = () => useContext(AppSettingsContext)!

export default useAppSettings
